import { gql } from '@apollo/client';
import { useFaustQuery } from '@faustwp/core';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { SEO } from '../components';
import { Post } from '../components';
import { BlogPostPage } from '../components/BlogPostPage';
import { CaseStudy, Footer, Header } from '../components/sections';
import { useLanguage } from '../contexts/LanguageContext';
import { BlogInfoFragment } from '../fragments/GeneralSettings';
import { BLOG_BASE_CATEGORY_EN, BLOG_BASE_CATEGORY_FI } from '../constants/blogCategories';
import { FINNISH_CATEGORY_SLUG, ENGLISH_CATEGORY_SLUG, getLangFromPath } from '../lib/caseStudyPaths';
import { mapPostToCaseStudy } from '../lib/caseStudyMapping';
import { getCanonicalUrl } from '../lib/siteUrl';

const GET_LAYOUT_QUERY = gql`
  ${BlogInfoFragment}
  query GetLayout {
    generalSettings {
      ...BlogInfoFragment
    }
  }
`;

// Query without ACF so the page works when WPGraphQL for ACF isn't ready.
// To enable Customer story ACF: install/activate WPGraphQL for ACF, set the field group
// "Show in GraphQL" + GraphQL Type Name "CaseStudy", then uncomment the block below
// and use: const raw = post?.caseStudy;
const GET_POST_QUERY = gql`
  query GetPost($databaseId: ID!, $asPreview: Boolean = false) {
    post(id: $databaseId, idType: DATABASE_ID, asPreview: $asPreview) {
      title
      content
      date
      excerpt
      uri
      slug
      language {
        code
      }
      translations {
        language {
          code
        }
        uri
        slug
      }
      featuredImage {
        node {
          sourceUrl
        }
      }
      categories {
        nodes {
          slug
          name
        }
      }
      author {
        node {
          name
        }
      }
      blogPostEn {
        authorShown
        heroImage {
          node {
            sourceUrl
          }
        }
        blogPostColorTheme
        ctaTitle
        ctaDescription
        ctaButtonText
        ctaButtonUrl
      }
      # Uncomment when WPGraphQL for ACF exposes WithAcfCaseStudy:
      # ... on WithAcfCaseStudy {
      #   caseStudy {
      #     client
      #     headerImage { node { sourceUrl } }
      #     highlightColorDark
      #     highlightColorLight
      #     title
      #     description
      #     ourWork
      #     ourWorkFi
      #     ourWorkEn
      #     firstContentBox
      #     technology1
      #     technology2
      #     technology3
      #     technology4
      #     technology5
      #     technology6
      #     quote
      #     quoter
      #     secondContentBox
      #     dividerImage { node { sourceUrl } }
      #     summary1
      #     summary2
      #     summary3
      #     summary4
      #   }
      # }
    }
  }
`;

export default function Component(props) {
  if (props.loading) {
    return <>Loading...</>;
  }

  const { generalSettings } = useFaustQuery(GET_LAYOUT_QUERY) ?? {};
  const { post } = useFaustQuery(GET_POST_QUERY) ?? {};
  const { title: siteTitle, description: siteDescription } =
    generalSettings ?? {};
  const postTitle = post?.title;
  const imageUrl = post?.featuredImage?.node?.sourceUrl;

  const categorySlugs = post?.categories?.nodes?.map((c) => c.slug) ?? [];
  const isCaseStory =
    categorySlugs.includes(FINNISH_CATEGORY_SLUG) ||
    categorySlugs.includes(ENGLISH_CATEGORY_SLUG);
  const isBlogPost =
    categorySlugs.includes(BLOG_BASE_CATEGORY_EN) ||
    categorySlugs.includes(BLOG_BASE_CATEGORY_FI);
  const blogLang = categorySlugs.includes(BLOG_BASE_CATEGORY_FI) ? 'fi' : 'en';

  // ACF case study data (set raw = post?.caseStudy when ACF fragment is enabled)
  const raw = null; // post?.caseStudy
  const caseStudy = mapPostToCaseStudy(raw);

  const router = useRouter();
  const [lang] = useLanguage();
  // Lang from URL: /en/... => EN, else FI (so "Our work" matches the page)
  const pathLang = getLangFromPath(router?.asPath ?? post?.uri ?? '');
  const caseStudyLang = pathLang ?? lang;

  if (isBlogPost) {
    return (
      <BlogPostPage
        post={post}
        generalSettings={generalSettings}
        relatedPosts={[]}
        lang={blogLang}
      />
    );
  }

  const canonicalUrl = post?.uri ? getCanonicalUrl(post.uri) : undefined;

  return (
    <>
      <SEO
        title={postTitle ? `${postTitle} | ${siteTitle}` : siteTitle}
        description={siteDescription}
        imageUrl={imageUrl}
        url={canonicalUrl}
      />
      <div className="min-h-screen bg-white">
        <Header />
        {isCaseStory ? (
          <CaseStudy
            post={{ title: postTitle, featuredImage: imageUrl }}
            caseStudy={caseStudy}
            lang={caseStudyLang}
          />
        ) : (
          <>
            <nav className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between border-b border-gray-200">
              <Link href="/" className="text-[#6b7280] hover:text-[#1a1a1a]">
                ← Back
              </Link>
            </nav>
            <main className="max-w-4xl mx-auto px-6 py-12">
              <Post
                title={postTitle}
                content={post?.content}
                date={post?.date}
                author={post?.author?.node?.name}
                uri={post?.uri}
                featuredImage={imageUrl ? { sourceUrl: imageUrl } : null}
              />
            </main>
          </>
        )}
        <Footer />
      </div>
    </>
  );
}

Component.queries = [
  {
    query: GET_LAYOUT_QUERY,
    variables: () => ({}),
  },
  {
    query: GET_POST_QUERY,
    variables: ({ databaseId }, ctx) => ({
      databaseId,
      asPreview: ctx?.asPreview,
    }),
  },
];
